import React, { useState, useRef } from 'react';
import Webcam from 'react-webcam';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { QuickBooksSyncHint } from './QuickBooksSyncHint';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Plus,
  Receipt,
  Camera,
  Upload,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Loader2,
  Sparkles,
  Calendar,
  StickyNote
} from 'lucide-react';
import { toast } from 'sonner';
import apiV2, { orgApi } from '../utils/apiV2';
import { useExpenses, useInvalidateFinancials } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';

// Expense categories
const EXPENSE_CATEGORIES = [
  { id: 'fuel', label: 'Fuel', color: 'bg-amber-100 text-amber-700' },
  { id: 'maintenance', label: 'Maintenance', color: 'bg-blue-100 text-blue-700' },
  { id: 'insurance', label: 'Insurance', color: 'bg-purple-100 text-purple-700' },
  { id: 'supplies', label: 'Supplies', color: 'bg-green-100 text-green-700' },
  { id: 'labor', label: 'Labor', color: 'bg-pink-100 text-pink-700' },
  { id: 'salary', label: 'Employee Salary', color: 'bg-rose-100 text-rose-700' },
  { id: 'rent', label: 'Rent', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'marketing', label: 'Marketing', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'utilities', label: 'Utilities', color: 'bg-amber-100 text-amber-700' },
  { id: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ExpensesTab = ({ onNavigate }) => {
  // Date range
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 15;
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewExpense, setViewExpense] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  
  // Refs
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'other',
    expense_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Currency + country from the operator's org (single source of truth)
  const { currencyCode: currency, countryCode } = useOperatorLocale();
  
  // Fetch expenses via shared hook — canonical key shared with P&L so they
  // dedupe + stay in sync.
  const { expenses, isLoading, isError: error, refresh: mutate } = useExpenses(
    dateRange.startDate,
    dateRange.endDate
  );
  // Refresh every financial surface (Dashboard, Revenue, P&L) after a write
  const invalidateFinancials = useInvalidateFinancials();
  
  // Calculate total
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Pagination
  const totalPages = Math.ceil(expenses.length / ITEMS_PER_PAGE);
  const paginatedExpenses = expenses.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  
  // Reset form
  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      category: 'other',
      expense_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingExpense(null);
    setCameraActive(false);
    setScanLoading(false);
  };
  
  // Open edit modal
  const openEditModal = (expense) => {
    setViewExpense(null);
    setEditingExpense(expense);
    setFormData({
      description: expense.title || expense.description || '',
      amount: expense.amount?.toString() || '',
      category: expense.category || 'other',
      expense_date: (expense.date || expense.expense_date)?.split('T')[0] || new Date().toISOString().split('T')[0],
      notes: expense.notes || ''
    });
    setShowAddModal(true);
  };
  
  // Save expense
  const handleSave = async () => {
    if (!formData.description || !formData.amount) {
      toast.error('Please fill in description and amount');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        title: formData.description,
        amount: parseFloat(formData.amount),
        date: formData.expense_date,
        category: formData.category,
        notes: formData.notes,
        currency: currency
      };
      
      if (editingExpense) {
        await orgApi.updateExpense(editingExpense.id, payload);
        toast.success('Expense updated!');
      } else {
        await orgApi.createExpense(payload);
        toast.success('Expense added!');
      }
      
      mutate();
      invalidateFinancials();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error('Save expense error:', err);
      toast.error('Failed to save expense');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Delete expense
  const handleDelete = async (expense) => {
    if (!confirm('Delete this expense?')) return;
    
    try {
      await orgApi.deleteExpense(expense.id);
      toast.success('Expense deleted');
      setViewExpense(null);
      mutate();
      invalidateFinancials();
    } catch (err) {
      console.error('Delete expense error:', err);
      toast.error('Failed to delete expense');
    }
  };
  
  // ── Receipt scanning (AI autofill, inside the Add modal) ──
  const processReceiptImage = async (imageData) => {
    setScanLoading(true);
    try {
      const res = await apiV2.post('/expenses/parse-receipt', {
        image: imageData,
        defaultCurrency: currency,
        orgCountry: countryCode || 'US',
      });
      const parsed = res.data?.parsed;
      if (res.data?.success && parsed) {
        setFormData((prev) => ({
          ...prev,
          description: parsed.title || prev.description,
          amount: parsed.amount != null ? String(parsed.amount) : prev.amount,
          category: parsed.category || prev.category,
          expense_date: parsed.date || prev.expense_date,
          notes: prev.notes || 'Scanned from receipt',
        }));
        toast.success('Receipt scanned — review and save');
      } else {
        toast.error('Could not read that receipt clearly. Try again or enter manually.');
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Failed to scan receipt');
    } finally {
      setScanLoading(false);
      setCameraActive(false);
    }
  };
  
  const captureReceipt = async () => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error('Failed to capture image');
      return;
    }
    await processReceiptImage(imageSrc);
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => processReceiptImage(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  
  // Export functions
  const exportCSV = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Description', 'Category', 'Amount', 'Notes'];
    const rows = expenses.map(e => [
      e.date || e.expense_date || '',
      (e.title || e.description || '').replace(/,/g, ' '),
      e.category || '',
      e.amount || 0,
      (e.notes || '').replace(/,/g, ';')
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('Expenses exported as CSV!');
  };
  
  const exportJSON = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: { totalExpenses: totalExpenses, expenseCount: expenses.length },
      expenses: expenses.map(e => ({
        date: e.date || e.expense_date,
        description: e.title || e.description,
        category: e.category,
        amount: e.amount,
        notes: e.notes
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('Expenses exported as JSON!');
  };
  
  const exportPDF = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; border-bottom: 1px solid #ef4444; padding-bottom: 10px; }
          .summary { background: #fef2f2; padding: 15px; border-radius: 8px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f9fafb; text-align: left; padding: 12px; border-bottom: 1px solid #e5e7eb; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          .amount { color: #ef4444; text-align: right; }
          .category { padding: 4px 8px; border-radius: 12px; font-size: 12px; background: #f3f4f6; }
        </style>
      </head>
      <body>
        <h1>Expense Report</h1>
        <p>Period: ${dateRange.startDate} to ${dateRange.endDate}</p>
        <div class="summary">
          <strong>Total Expenses:</strong> ${formatCurrency(totalExpenses, currency)}<br>
          <strong>Items:</strong> ${expenses.length}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th style="text-align:right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${expenses.map(e => {
              const cat = getCategoryInfo(e.category);
              return `
                <tr>
                  <td>${formatDate(e.date || e.expense_date)}</td>
                  <td>${e.title || e.description || '—'}</td>
                  <td><span class="category">${cat.label}</span></td>
                  <td class="amount">${formatCurrency(e.amount, currency)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Get category info
  const getCategoryInfo = (categoryId) => {
    return EXPENSE_CATEGORIES.find(c => c.id === categoryId) || EXPENSE_CATEGORIES.find(c => c.id === 'other');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load expenses');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Expenses" subtitle="Track your costs">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  return (
    <TabPageLayout 
      title="Expenses" 
      subtitle="Track your costs"
      actions={
        <div className="flex gap-2">
          <ExportMenu onExport={(f) => ({ csv: exportCSV, pdf: exportPDF, json: exportJSON }[f]())} />
          <Button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add</span>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker + Total */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <DateRangeControl
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onStartChange={(v) => { setDateRange({ ...dateRange, startDate: v }); setPage(1); }}
            onEndChange={(v) => { setDateRange({ ...dateRange, endDate: v }); setPage(1); }}
          />
          
          {/* Total Expenses Badge */}
          <div className="px-4 py-2 rounded-full bg-red-50" data-testid="expenses-total-badge">
            <span className="text-lg font-medium text-red-600">
              {formatCurrency(totalExpenses, currency)}
            </span>
            <span className="text-sm ml-2 text-red-500/70">
              Total Expenses
            </span>
          </div>
        </div>

        <QuickBooksSyncHint variant="expenses" onNavigate={onNavigate} />
        
        {/* Expenses Table */}
        <div className="card-soft overflow-hidden">
          {expenses.length === 0 ? (
            <EmptyState
              icon={<Receipt className="w-10 h-10" />}
              title="No expenses yet"
              description="Add your first expense to start tracking"
              action={
                <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white h-10" data-testid="empty-add-expense-btn">
                  <Plus className="w-4 h-4" />
                  Add Expense
                </Button>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedExpenses.map((expense, i) => {
                      const cat = getCategoryInfo(expense.category);
                      return (
                        <tr
                          key={expense.id}
                          onClick={() => setViewExpense(expense)}
                          data-testid={`expense-row-${i}`}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 text-gray-900">{formatDate(expense.date || expense.expense_date)}</td>
                          <td className="px-4 py-3 text-gray-900">{expense.title || expense.description || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${cat.color}`}>
                              {cat.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            {formatCurrency(expense.amount, currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, expenses.length)} of {expenses.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-9"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Add/Edit Expense Modal */}
      <Dialog open={showAddModal} onOpenChange={(o) => { setShowAddModal(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-md" data-testid="expense-form-modal">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update expense details' : 'Enter details — or snap a receipt and let AI fill it in'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* AI receipt autofill — only when adding */}
            {!editingExpense && (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/70 p-3" data-testid="receipt-autofill-section">
                {cameraActive ? (
                  <div className="space-y-3">
                    <div className="rounded-lg overflow-hidden bg-black">
                      <Webcam
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        className="w-full"
                        videoConstraints={{ facingMode: 'environment' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => setCameraActive(false)} variant="outline" className="flex-1 h-9" data-testid="receipt-camera-cancel">
                        Cancel
                      </Button>
                      <Button onClick={captureReceipt} disabled={scanLoading} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid="receipt-capture-btn">
                        {scanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Capture'}
                      </Button>
                    </div>
                  </div>
                ) : scanLoading ? (
                  <div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-600" data-testid="receipt-scan-loading">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    Reading receipt…
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
                      <Sparkles className="w-3.5 h-3.5 text-blue-500" />
                      Autofill from a receipt
                    </span>
                    <div className="flex gap-2 ml-auto">
                      <Button onClick={() => setCameraActive(true)} variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="receipt-snap-btn">
                        <Camera className="w-3.5 h-3.5" /> Snap
                      </Button>
                      <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="receipt-upload-btn">
                        <Upload className="w-3.5 h-3.5" /> Upload
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="receipt-file-input"
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Description <span className="text-red-500">*</span></label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Office supplies"
                className="mt-1"
                data-testid="expense-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Amount <span className="text-red-500">*</span></label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="expense-amount-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date</label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="mt-1"
                  data-testid="expense-date-input"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="mt-1" data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
                className="mt-1"
                data-testid="expense-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }} className="h-10">
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSubmitting}
              className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="expense-save-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : (
                editingExpense ? 'Update' : 'Add Expense'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense detail modal */}
      <Dialog open={!!viewExpense} onOpenChange={(o) => { if (!o) setViewExpense(null); }}>
        <DialogContent className="max-w-md" data-testid="expense-detail-modal">
          {viewExpense && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">{viewExpense.title || viewExpense.description || 'Expense'}</DialogTitle>
                <DialogDescription className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getCategoryInfo(viewExpense.category).color}`}>
                    {getCategoryInfo(viewExpense.category).label}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 flex items-center justify-between" data-testid="expense-detail-amount">
                <span className="text-sm text-gray-600">Amount</span>
                <span className="text-lg font-bold text-red-600 tabular-nums">
                  {formatCurrency(viewExpense.amount, currency)}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {formatDate(viewExpense.date || viewExpense.expense_date)}
                </div>
                {viewExpense.notes && (
                  <div className="flex items-start gap-2">
                    <StickyNote className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="leading-relaxed">{viewExpense.notes}</span>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleDelete(viewExpense)}
                  className="h-10 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="expense-detail-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
                <Button
                  onClick={() => openEditModal(viewExpense)}
                  className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="expense-detail-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default ExpensesTab;
